import { useContext, useEffect } from "react";
import { useDispatch } from "react-redux";

import * as constant from "../constants/editor";
import { widgetConfig } from "../components/Editor/editor_config";
import {
  changeWidgetColorAccordingBackground,
  getCssTextObjToString,
  getRandomString,
  hexToRGB,
} from "../../src/_helpers/utils";
import { EditorContext } from "../containers/editor/EditorLayout";
import useWidgetHandler from "./useWidgetHandler";
import { triggerUnsplashDownload } from "../store/actions/resourcesActions";
import { EditorPanelContext } from "../containers/editor/EditorPanel";
import { EditorCanvasPanelContext } from "../containers/editor/EditorCanvasPanel";

const useElementInnerHtml = (
  addWidgetClicked,
  setAddWidgetClicked,
  assettype,
  innerContent,
  param,
  widgetObject,
  customCss = {},
  brandText,
  cusetomClass
) => {
  const dispatch = useDispatch();
  let { metadata, backgroundColors, widgets, updateWidgets, updateMetadata, setClonedObjRef, isDocumentSigned } =
    useContext(EditorContext);

  const { toggleEventHandlers: toggleWidgetEventHandlers } = useWidgetHandler();

  const getNode = targetHtml => {
    let node;
    if (targetHtml.querySelector(`rect`)) {
      node = targetHtml.querySelector(`rect`);
    } else if (targetHtml.querySelector(`circle`)) {
      node = targetHtml.querySelector(`circle`);
    } else if (targetHtml.querySelector(`polygon`)) {
      node = targetHtml.querySelector(`polygon`);
    } else if (targetHtml.querySelector(`ellipse`)) {
      node = targetHtml.querySelector(`ellipse`);
    } else if (targetHtml.querySelector(`path`)) {
      node = targetHtml.querySelector(`path`);
    }
    return node;
  };

  const clippingPicture = targetHtml => {
    let activeImageSrc = widgetConfig[assettype].placeholderImage;
    let pathId = getRandomString(8);
    let insidePathId = `inside_${getRandomString(8)}`;
    let node = getNode(targetHtml);
    node.setAttribute("id", pathId);

    const SVG_NS = "http://www.w3.org/2000/svg";
    const XLINK_NS = "http://www.w3.org/1999/xlink";

    // --- defs ---
    const defs = document.createElementNS(SVG_NS, "defs");

    // --- clipPath ---
    const clipPath = document.createElementNS(SVG_NS, "clipPath");
    clipPath.setAttribute("id", insidePathId);

    const use = document.createElementNS(SVG_NS, "use");
    use.setAttributeNS(XLINK_NS, "xlink:href", `#${pathId}`);
    clipPath.appendChild(use);

    defs.appendChild(clipPath);

    // --- image ---
    const image = document.createElementNS(SVG_NS, "image");
    // cross-browser href
    image.setAttributeNS(null, "href", activeImageSrc);
    image.setAttributeNS(XLINK_NS, "xlink:href", activeImageSrc);

    image.setAttribute("x", "0");
    image.setAttribute("y", "0");
    image.setAttribute("width", "512");
    image.setAttribute("height", "512");
    image.setAttribute("clip-path", `url(#${insidePathId})`);
    image.setAttribute("preserveAspectRatio", "xMidYMid slice");

    // --- build final SVG ---
    targetHtml.innerHTML = ""; // clear old content
    targetHtml.appendChild(defs);
    targetHtml.appendChild(node); // clipping shape visible inside main SVG
    targetHtml.appendChild(image); // image that gets clipped

    targetHtml.setAttribute("id", `svg_${getRandomString(8)}`);
    targetHtml.setAttribute("viewBox", "0 0 512 512");

    return targetHtml;
  };

  useEffect(() => {
    if (addWidgetClicked && widgetObject) {
      let tag,
        classList,
        cssText,
        dataAttribute,
        node,
        widgetColor,
        activeBlockColor,
        cssValue = "";
      let index = backgroundColors.findIndex(backgroundColor => backgroundColor.blockId === metadata.activeBlockId);

      if (index !== -1) activeBlockColor = hexToRGB(backgroundColors[index]?.style.backgroundColor);
      widgetColor = changeWidgetColorAccordingBackground(activeBlockColor);
      setAddWidgetClicked && setAddWidgetClicked(false);

      // Set tag, classlist, style, data attribute for TEXT widget innerContent
      if (constant.TEXT === assettype) {
        tag = widgetConfig[assettype][param["data-heading"]].tag;
        classList = widgetConfig[assettype][param["data-heading"]].innerHTML.classList;
        cssText = {
          ...widgetConfig[assettype][param["data-heading"]].innerHTML.style,
          ...customCss,
          color: widgetColor,
        };
        dataAttribute = widgetConfig[assettype][param["data-heading"]].innerHTML.dataAttr;
      }
      // Set tag, classlist, style, data attribute for Video widget innerContent
      else if (constant.VIDEO === assettype) {
        tag = widgetConfig[assettype][param["data-source"]].tag;
        classList = widgetConfig[assettype][param["data-source"]].innerHTML.classList;
        cssText = widgetConfig[assettype][param["data-source"]].innerHTML.style;
        dataAttribute = widgetConfig[assettype][param["data-source"]].innerHTML.dataAttr;
      }
      // Set tag, classlist, style, data attribute for UPLOADED SVG widget innerContent
      else if (constant.UPLOAD === assettype && param["data-file-type"] === constant.SVG) {
        tag = widgetConfig[assettype][param["data-file-type"]].tag;
        classList = widgetConfig[assettype][param["data-file-type"]].innerHTML.classList;
        cssText = { ...widgetConfig[assettype][param["data-file-type"]].innerHTML.style, fill: widgetColor };
        dataAttribute = widgetConfig[assettype][param["data-file-type"]].innerHTML.dataAttr;
      }
      // Set tag, classlist, style, data attribute for MAP widget innerContent
      else if (constant.MAP === assettype) {
        tag = widgetConfig[assettype].tag;
        classList = cusetomClass
          ? `${widgetConfig[assettype].innerHTML.classList} ${cusetomClass}`
          : widgetConfig[assettype].innerHTML.classList;
        dataAttribute = widgetConfig[assettype].innerHTML.dataAttr;
        cssText = { ...widgetConfig[assettype].innerHTML.style, ...customCss };
      }
      // Set tag, classlist, style, data attribute for pricing table widget innerContent
      else if (
        [
          constant.PRICING_TABLE,
          constant.TIMELINE,
          constant.SCOPE_OF_WORK,
          constant.ITINERARY,
          constant.PORTFOLIO,
          constant.CASESTUDY,
        ].includes(assettype)
      ) {
        tag = widgetConfig[assettype].tag;
        classList = widgetConfig[assettype].innerHTML.classList;
        dataAttribute = widgetConfig[assettype].innerHTML.dataAttr;
        cssText = { ...widgetConfig[assettype].innerHTML.style, ...customCss };
      }
      // Set tag, classlist, style, data attribute for OTHER widget innerContent
      else {
        if (!assettype) return; // Return if assetType is not available
        tag = widgetConfig[assettype].tag;
        classList = widgetConfig[assettype].innerHTML.classList;
        dataAttribute = widgetConfig[assettype].innerHTML.dataAttr;
        cssText =
          widgetConfig[assettype].tag === "svg"
            ? { ...widgetConfig[assettype].innerHTML.style, fill: assettype !== constant.FRAME ? widgetColor : "none" }
            : widgetConfig[assettype].innerHTML.style;

        //for any mono icon drop in dark background
        if (widgetConfig[assettype].tag === "svg" && param?.["data-scheme"] === constant.MONO) {
          innerContent.querySelectorAll("*").forEach(function (node) {
            if (node.style?.fill) node.style.fill = widgetColor;
          });
        }
      }

      // Create Node
      if (tag !== "svg") {
        node = document.createElement(tag);
        if ([constant.TEXT, constant.TYPE_FORM, constant.JOT_FORM, constant.TEXT_ART].includes(assettype))
          node.innerHTML = innerContent;
        else if (
          [
            constant.TABLE,
            constant.COLLAGE,
            constant.MAP,
            constant.PRICING_TABLE,
            constant.TIMELINE,
            constant.SCOPE_OF_WORK,
            constant.ITINERARY,
            constant.PORTFOLIO,
            constant.CASESTUDY,
          ].includes(assettype)
        )
          node = innerContent;
        else if (
          [constant.PICTURE, constant.UPLOAD, constant.VIDEO, constant.ANIMATION].includes(assettype) &&
          param["data-file-type"] !== constant.SVG &&
          param["data-source"] !== constant.YOUTUBE
        ) {
          let wrapperDiv = document.createElement("div");
          wrapperDiv.classList.add("dhp-widget-inner");
          let wrapper = constant.VIDEO === assettype ? document.createElement("video") : document.createElement("img");
          if (constant.VIDEO === assettype) wrapper.poster = param["data-poster"];

          wrapperDiv.style.cssText = getCssTextObjToString({
            ...cssText,
            height: constant.ANIMATION === assettype ? "100%" : `${widgetObject.style.height}px`,
            width: constant.ANIMATION === assettype ? "100%" : `${widgetObject.style.width}px`,
          });

          wrapper.style.cssText =
            constant.VIDEO === assettype
              ? getCssTextObjToString(widgetConfig[assettype][constant.STOCK].childElement.style)
              : getCssTextObjToString(widgetConfig[assettype].childElement.style);

          cssText = {
            height: `${widgetObject.style.height}px`,
            width: `${widgetObject.style.width}px`,
            overflow: "hidden",
          };

          // for video type widgets, setting default poster image frame to default time i.e. 0.1 sec
          if (
            constant.VIDEO === assettype &&
            [constant.STOCK, constant.BRAND, constant.UPLOAD_VIDEO].includes(param["data-source"])
          ) {
            wrapper.src = `${innerContent}#t=${constant.VIDEO_DEFAULT_TRIM_START_TIME}`;
          } else wrapper.src = innerContent;

          // wrapper.src = innerContent;
          wrapperDiv.appendChild(wrapper);
          node.appendChild(wrapperDiv);
        } else node.src = innerContent;
      } else {
        if (assettype === constant.FRAME) {
          let newInnercontent = clippingPicture(innerContent);
          node = newInnercontent;
        } else node = innerContent;
      }

      cssValue = getCssTextObjToString(cssText);
      node.classList = classList;

      if (constant.ANIMATION !== assettype && constant.QR_CODE !== assettype) {
        node.style.cssText = cssValue;
      }

      for (const [key, value] of Object.entries(dataAttribute)) {
        if (constant.VIDEO === assettype && param["data-source"] !== constant.YOUTUBE)
          node.getElementsByTagName("video")[0].setAttribute(key, value);
        else node.setAttribute(key, value);
      }

      let newWidgetObj;
      // Apply dynamic height for brand text insertion
      if (constant.TEXT === assettype && cssValue && brandText) {
        let phedoElem = document.createElement("div");
        phedoElem.id = "p1";
        phedoElem.style.cssText = "visibility:hidden";
        phedoElem.innerHTML = node.outerHTML;

        $(`#${metadata.activeBlockId}`).append(phedoElem.outerHTML);
        let elemHeight = document.querySelector("#p1 .primary-text-widget").offsetHeight;
        document.getElementById("p1").remove();

        newWidgetObj = {
          ...widgetObject,
          style: {
            ...widgetObject.style,
            height: `${elemHeight}px`,
          },
          innerHTML: node.outerHTML,
        };
      } else if (constant.CHART === assettype) {
        newWidgetObj = {
          ...widgetObject,
          data: { ...widgetObject.data, "data-chart-type": innerContent.chartType },
          innerHTML: node.outerHTML,
          chartConfig: innerContent,
          chartThumb: innerContent.chartDataImage,
        };
      } else {
        newWidgetObj = { ...widgetObject, innerHTML: node.outerHTML };
      }

      if (isDocumentSigned) return;

      updateWidgets([...widgets, newWidgetObj]);
      updateMetadata({ ...metadata, activeWidgetId: [widgetObject.id], activeWidgetType: [assettype] });
      setClonedObjRef(false);
      setTimeout(() => {
        toggleWidgetEventHandlers({ action: "SHOW" });
        // video will play after insert
        if (
          constant.VIDEO === assettype &&
          [constant.STOCK, constant.BRAND, constant.UPLOAD_VIDEO].includes(param["data-source"])
        ) {
          document.getElementById(widgetObject.id).setAttribute("data-playing", true);
          document.querySelector(`#${widgetObject.id} video`).setAttribute("loop", "loop");
        }

        // trigger unsplash download
        if (assettype === constant.PICTURE && param["data-origin"] === "unsplash") {
          dispatch(triggerUnsplashDownload(param["data-download"]));
        }
      }, 50);
    }
  }, [widgetObject]);
};

export default useElementInnerHtml;
